import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { enqueueSnackbar } from "notistack";
import { useCallback, useState } from "react";
import { useSearchParams } from "react-router";
import { useAppConfig, useCampground } from "@/contexts/AppConfigContext";
import { useBookingContext } from "@/contexts/BookingContext";
import { ApiError, api } from "@/services/api";
import type { CheckoutErrorResponse } from "@/types/api";
import { buildCheckoutData } from "@/utils/booking";

interface UseDraftBookingReturn {
  isDraftBookingEnabled: boolean;
  isSaving: boolean;
  draftBookingLink: string | null;
  saveDraft: () => Promise<void>;
  clearDraftLink: () => void;
}

export function useDraftBooking(): UseDraftBookingReturn {
  const { aid } = useAppConfig();
  const { state, actions } = useBookingContext();
  const { data: campgroundData } = useCampground();
  const { i18n } = useLingui();
  const [isSaving, setIsSaving] = useState(false);
  const [draftBookingLink, setDraftBookingLink] = useState<string | null>(null);

  const [searchParams] = useSearchParams();
  const isDraftBookingEnabled =
    campgroundData.campground.checkout_config?.DraftBooking === true &&
    searchParams.has("enable_draftbooking");

  const clearDraftLink = useCallback(() => setDraftBookingLink(null), []);

  const saveDraft = useCallback(async () => {
    const checkoutData = buildCheckoutData(state, aid);
    if (!checkoutData) return;

    if (state.price) {
      checkoutData.draft_booking_pricelist = state.price;
    }

    const separator = campgroundData.urls.checkout.includes("?") ? "&" : "?";
    const tokenParam = state.draftBookingToken
      ? `&draft_booking_id=${encodeURIComponent(state.draftBookingToken)}`
      : "";
    const url = `${campgroundData.urls.checkout}${separator}draft_booking=true${tokenParam}`;

    setIsSaving(true);
    try {
      const response = await api.submitCheckout(url, checkoutData);

      const token = response.draft_booking?.draft_booking_id ?? response.draft_booking?.id;
      if (token) {
        actions.setDraftBookingToken(token);
      }

      if (response.draft_booking_link) {
        let link = response.draft_booking_link;
        try {
          const serverUrl = new URL(response.draft_booking_link);
          serverUrl.host = window.location.host;
          serverUrl.protocol = window.location.protocol;
          link = serverUrl.toString();
        } catch {
          // malformed URL from server — use as-is
        }
        setDraftBookingLink(link);
      } else {
        enqueueSnackbar(i18n._(msg`Booking saved as draft.`), { variant: "success" });
      }
    } catch (err) {
      let message = i18n._(msg`Could not save your booking. Please try again.`);
      if (err instanceof ApiError) {
        const apiError = err as ApiError<CheckoutErrorResponse>;
        const fieldError = apiError.data?.non_field_errors?.[0];
        if (fieldError) message = fieldError;
      }
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setIsSaving(false);
    }
  }, [state, aid, campgroundData.urls.checkout, actions, i18n]);

  return {
    isDraftBookingEnabled,
    isSaving,
    draftBookingLink,
    saveDraft,
    clearDraftLink,
  };
}
